import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaUserShield, FaCheckSquare, FaSquare, FaSave, FaSearch,
  FaUserCheck, FaBuilding, FaExclamationTriangle, FaRibbon, FaUnlockAlt
} from 'react-icons/fa';

interface Member {
  id: number;
  name: string;
  email: string;
  company_name?: string;
  profile?: string;
  role?: string;
  sub_role?: string;
  designation?: string;
  status?: string;
}

interface PermissionModule {
  key: string;
  label: string;
  category: string;
  description: string;
}

const PRESET_ROLES = [
  {
    name: 'Executive Officer',
    role: 'Leader',
    sub_role: 'Executive Officer',
    permissions: ['manage_members', 'manage_audit_ledger', 'verify_activities', 'manage_dynamic_forms', 'manage_events', 'manage_ai_flyer', 'manage_gallery', 'manage_points', 'view_reports', 'manage_rbac']
  },
  {
    name: 'Committee Coordinator',
    role: 'Member',
    sub_role: 'Coordinator',
    permissions: ['verify_activities', 'manage_dynamic_forms', 'manage_events', 'manage_ai_flyer', 'manage_gallery', 'manage_points']
  },
  {
    name: 'Finance & Audit Lead',
    role: 'Leader',
    sub_role: 'Treasurer',
    permissions: ['manage_audit_ledger', 'manage_dynamic_forms', 'view_reports']
  },
  {
    name: 'Activity Verifier',
    role: 'Member',
    sub_role: 'Verifier Lead',
    permissions: ['verify_activities', 'manage_points']
  },
  {
    name: 'Standard Member',
    role: 'Member',
    sub_role: '',
    permissions: []
  }
];

const RbacManagement: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [modules, setModules] = useState<PermissionModule[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  // Selected Member Form State
  const [role, setRole] = useState('Member');
  const [subRole, setSubRole] = useState('');
  const [assignedPermissions, setAssignedPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [membersRes, modulesRes] = await Promise.all([
        axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo'),
        axios.get('https://rmbbackend.rmbjaipur.com/auth/rbac/permissions-list')
      ]);

      const activeList = (Array.isArray(membersRes.data) ? membersRes.data : [])
        .filter((m: any) => m.delete !== 1);

      setMembers(activeList);
      setModules(modulesRes.data.modules || []);

      if (activeList.length > 0) {
        selectMember(activeList[0]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching RBAC initial data:', err);
      setLoading(false);
    }
  };

  const selectMember = async (member: Member) => {
    setSelectedMember(member);
    setSuccessMsg('');
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/users/${member.id}/permissions`);
      setRole(res.data.role || member.role || 'Member');
      setSubRole(res.data.sub_role || member.sub_role || '');
      setAssignedPermissions(res.data.permissions || []);
    } catch (err) {
      setRole(member.role || 'Member');
      setSubRole(member.sub_role || '');
      setAssignedPermissions([]);
    }
  };

  const togglePermission = (key: string) => {
    if (assignedPermissions.includes(key)) {
      setAssignedPermissions(assignedPermissions.filter(k => k !== key));
    } else {
      setAssignedPermissions([...assignedPermissions, key]);
    }
  };

  const applyPreset = (preset: typeof PRESET_ROLES[0]) => {
    setRole(preset.role);
    setSubRole(preset.sub_role);
    setAssignedPermissions([...preset.permissions]);
  };

  const handleSavePermissions = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;

    try {
      setSaving(true);
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/users/${selectedMember.id}/permissions`, {
        role,
        sub_role: subRole,
        permissions: assignedPermissions
      });

      // Update local member list
      setMembers(prev => prev.map(m => m.id === selectedMember.id ? { ...m, role, sub_role: subRole } : m));
      setSelectedMember(prev => prev ? { ...prev, role, sub_role: subRole } : null);

      setSaving(false);
      setSuccessMsg('Permissions & Responsibilities saved successfully!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Error saving permissions:', err);
      alert('Failed to save member permissions.');
      setSaving(false);
    }
  };

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.company_name && m.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6 bg-slate-50 min-h-screen text-slate-800">
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0A2451] flex items-center gap-2">
            <FaUserShield className="text-[#FDBC12]" /> Role-Based Access Control (RBAC) & Responsibilities
          </h1>
          <p className="text-xs md:text-sm text-slate-500 font-medium mt-1">
            Assign custom operational roles, sub-roles, and fine-grained administrative permissions to chapter members.
          </p>
        </div>

        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 animate-bounce">
            <FaUserCheck /> {successMsg}
          </div>
        )}
      </div>

      {loading ? (
        <div className="bg-white p-12 rounded-2xl text-center text-slate-500 font-bold">
          Loading RBAC data & member roster...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Member Roster List Sidebar */}
          <div className="lg:col-span-4 bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[700px]">
            <div className="mb-4">
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-2">Select Member</h2>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search member by name or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-[#0A2451] outline-none"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
              {filteredMembers.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No matching active members found.</p>
              ) : (
                filteredMembers.map((m) => {
                  const isSelected = selectedMember?.id === m.id;
                  return (
                    <div
                      key={m.id}
                      onClick={() => selectMember(m)}
                      className={`p-3 rounded-xl border transition cursor-pointer flex items-center gap-3 ${isSelected
                          ? 'bg-[#0A2451] text-white border-[#0A2451] shadow-md'
                          : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-800'
                        }`}
                    >
                      <img
                        src={
                          m.profile
                            ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`)
                            : 'https://via.placeholder.com/100?text=Member'
                        }
                        alt={m.name}
                        className="w-10 h-10 rounded-full object-cover border border-slate-300"
                        onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/100?text=Member'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-xs font-black truncate ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                          {m.name}
                        </h4>
                        <p className={`text-[11px] truncate ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                          {m.company_name || m.email}
                        </p>
                        {(m.sub_role || m.role) && (
                          <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${isSelected ? 'bg-[#FDBC12] text-[#0A2451]' : 'bg-slate-100 text-slate-700 border'
                            }`}>
                            {m.sub_role || m.role}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* RBAC Form & Responsibilities Matrix */}
          <div className="lg:col-span-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between">
            {selectedMember ? (
              <form onSubmit={handleSavePermissions} className="space-y-6">

                {/* Selected Member Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200 gap-4">
                  <div className="flex items-center gap-4">
                    <img
                      src={
                        selectedMember.profile
                          ? (selectedMember.profile.startsWith('http') ? selectedMember.profile : `https://rmbbackend.rmbjaipur.com/${selectedMember.profile.replace(/\\/g, '/')}`)
                          : 'https://via.placeholder.com/100?text=Member'
                      }
                      alt={selectedMember.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#FDBC12]"
                    />
                    <div>
                      <h2 className="text-lg font-black text-[#0A2451]">{selectedMember.name}</h2>
                      <p className="text-xs text-slate-500 font-semibold">{selectedMember.company_name || selectedMember.email}</p>
                      <span className="inline-block bg-[#0A2451] text-[#FDBC12] text-[10px] font-extrabold px-2.5 py-0.5 rounded-full mt-1">
                        Current Role: {role} {subRole ? `(${subRole})` : ''}
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-xs px-6 py-2.5 rounded-xl shadow-lg transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <FaSave className="text-[#FDBC12]" /> {saving ? 'Saving...' : 'Save Member Responsibilities'}
                  </button>
                </div>

                {/* Role Presets */}
                <div>
                  <label className="block text-xs font-black text-slate-700 uppercase mb-2">Quick Role Presets</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_ROLES.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-[#FDBC12] hover:text-[#0A2451] border border-slate-300 rounded-lg text-xs font-bold text-slate-700 transition"
                      >
                        ⚡ {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* System Role & Sub Role Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-amber-50/50 p-4 rounded-xl border border-amber-100">
                  <div>
                    <label className="block text-xs font-bold text-amber-900 uppercase mb-1">System Base Role</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-bold bg-white focus:ring-2 focus:ring-[#0A2451] outline-none"
                    >
                      <option value="Member">Member</option>
                      <option value="Leader">Leader / Officer</option>
                      <option value="Coordinator">Coordinator</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-amber-900 uppercase mb-1">Sub-Role / Official Title</label>
                    <input
                      type="text"
                      placeholder="e.g. Chairman, R2R Coordinator, Event Lead"
                      value={subRole}
                      onChange={(e) => setSubRole(e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-semibold bg-white focus:ring-2 focus:ring-[#0A2451] outline-none"
                    />
                  </div>
                </div>

                {/* Responsibilities & Granular Permission Checkboxes */}
                <div>
                  <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <FaUnlockAlt className="text-[#0A2451]" /> Assign Operational Responsibilities & Feature Permissions
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[380px] overflow-y-auto pr-1">
                    {modules.map((mod) => {
                      const isChecked = assignedPermissions.includes(mod.key);
                      return (
                        <div
                          key={mod.key}
                          onClick={() => togglePermission(mod.key)}
                          className={`p-3.5 rounded-xl border cursor-pointer transition flex items-start gap-3 ${isChecked
                              ? 'bg-blue-50/80 border-[#0A2451] text-slate-900 shadow-sm'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                            }`}
                        >
                          <div className="mt-0.5 text-base text-[#0A2451]">
                            {isChecked ? <FaCheckSquare className="text-emerald-600" /> : <FaSquare className="text-slate-300" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-black text-[#0A2451]">{mod.label}</span>
                              <span className="bg-slate-200 text-slate-700 text-[9px] font-extrabold px-2 py-0.5 rounded-full uppercase">
                                {mod.category}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1 leading-snug font-medium">
                              {mod.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer Save Action */}
                <div className="pt-4 border-t border-slate-200 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-xs px-8 py-3 rounded-xl shadow-lg transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <FaSave className="text-[#FDBC12]" /> {saving ? 'Saving...' : 'Save Member Responsibilities'}
                  </button>
                </div>

              </form>
            ) : (
              <div className="text-center text-slate-400 font-bold py-20">
                Select a member from the left roster to manage their RBAC roles and permissions.
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default RbacManagement;
